// frontend/src/hooks/chat/utils.ts
/**
 * Utility functions for chat hooks.
 */

import { getValidAccessToken, isPermanentRefreshError } from '../../lib/tokenManager';
import { handleAuthError } from '../../utils/authErrorHandler';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Chat message role
 */
export type ChatMessageRole = 'user' | 'assistant' | 'system';

/**
 * Chat message structure
 */
export interface ChatMessage {
  id: number;
  role: ChatMessageRole;
  content: string;
  timestamp: string;
  isStreaming?: boolean;
  /** Latest backend progress line ("Researching...", "Looking up CMSC422…"),
   *  shown in the bubble until the first text arrives. */
  statusText?: string;
  isError?: boolean;
  isAgentV4?: boolean;
  intent?: unknown;
  validation?: unknown;
  research?: unknown;
  metrics?: ChatMetrics | null;
  proposedActions?: ChatAction[];
  requiresConfirmation?: boolean;
  confirmedActions?: { executed: ChatAction[]; count: number };
  rejectedActions?: ChatAction[];
  wasCancelled?: boolean;
  debugEvents?: DebugEvent[];
}

/**
 * Chat action from Agent V4
 */
export interface ChatAction {
  action_type: string;
  parameters?: Record<string, unknown>;
}

/**
 * Debug event from Agent V4 verbose mode
 */
export interface DebugEvent {
  stage: string;
  [key: string]: unknown;
}

/**
 * Chat metrics
 */
export interface ChatMetrics {
  total_time_ms?: number;
  intent_time_ms?: number;
  validation_time_ms?: number;
  research_time_ms?: number;
  response_time_ms?: number;
  [key: string]: unknown;
}

/**
 * Available model info
 */
export interface ChatModel {
  id: string;
  name: string;
  description: string;
  default: boolean;
}

/**
 * Session stats
 */
export interface SessionStats {
  totalTokens: number;
  totalCost: number;
  messageCount: number;
  startTime: string;
}

/**
 * API message format
 */
export interface ApiMessage {
  role: ChatMessageRole;
  content: string;
}

// =============================================================================
// FUNCTIONS
// =============================================================================

/**
 * Get auth token for streaming requests via TokenManager.
 *
 * This delegates to the centralized TokenManager which:
 * - Caches tokens from onAuthStateChange
 * - Deduplicates concurrent refresh requests
 * - Distinguishes temporary vs permanent refresh errors
 *
 * This must be called with await since it's async.
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const token = await getValidAccessToken();
    return token;
  } catch (error) {
    console.error('Error getting access token:', error);

    // Only trigger auth error for permanent failures
    if (isPermanentRefreshError(error as Error)) {
      handleAuthError();
    }

    return null;
  }
};

/**
 * Get the sessionStorage key for chat messages.
 */
export const getChatStorageKey = (academicPlanId: number | string): string => `chat_messages_${academicPlanId}`;

/**
 * Load messages from sessionStorage.
 * Restores chat history for the current session to preserve context
 * during component remounts (e.g., plan refresh after action confirmation).
 */
export const loadMessagesFromStorage = (academicPlanId: number | string | null): ChatMessage[] => {
  if (!academicPlanId) return [];
  try {
    const stored = sessionStorage.getItem(getChatStorageKey(academicPlanId));
    if (stored) {
      const messages = JSON.parse(stored);
      // Validate that it's an array of messages
      if (Array.isArray(messages)) {
        return (messages as ChatMessage[]).map(settleInterruptedMessage);
      }
    }
  } catch (e) {
    console.warn('Failed to load chat messages from storage:', e);
  }
  return [];
};

/**
 * A message that was still streaming when it was persisted was interrupted.
 *
 * `isStreaming` is written to storage on every chunk, and whatever cleared it
 * (the stream finishing, or the abort on unmount) could only ever do so in
 * memory — once the hook is gone there is nothing left to update the stored
 * copy. So a thread restored mid-answer carries a half-sentence still flagged
 * as live, which renders a blinking cursor that never stops.
 *
 * Settling it on the way out of storage is the one chokepoint every restore
 * passes through, and it is where the truth is known: if it is still marked
 * streaming by the time it is being read back, the stream did not finish.
 *
 * The empty-content case — interrupted before the first token, a window
 * seconds wide because the first token waits on intent classification,
 * validation and possibly research — is deliberately NOT papered over here
 * with placeholder text. An earlier version did that, which solved two real
 * problems (a blank bubble, and an empty assistant turn reaching the API) by
 * writing display copy into stored data, and then read as two stacked
 * near-identical sentences. Each of those problems is now handled where it
 * belongs: `MessageContent` renders the interrupted notice for a message with
 * no content, and the conversation-history builder refuses to ship an empty
 * turn. This function only records what happened.
 */
const settleInterruptedMessage = (message: ChatMessage): ChatMessage =>
  message.isStreaming
    ? { ...message, isStreaming: false, wasCancelled: true }
    : message;

/**
 * Save messages to sessionStorage.
 *
 * An EMPTY list is a no-op, not a delete. The two are different events and
 * conflating them is what erased live threads: `useChat` renders empty for one
 * commit whenever it adopts a plan id it has not hydrated yet, and the old
 * `removeItem` branch turned that transient render into a permanent deletion of
 * the thread it was about to load. Clearing is now an explicit act with its own
 * function (`clearMessagesFromStorage`), so only the user can trigger it.
 */
export const saveMessagesToStorage = (academicPlanId: number | string | null, messages: ChatMessage[]): void => {
  if (!academicPlanId) return;
  if (messages.length === 0) return;
  try {
    // Only keep last 50 messages to avoid storage limits
    const toStore = messages.slice(-50);
    sessionStorage.setItem(getChatStorageKey(academicPlanId), JSON.stringify(toStore));
  } catch (e) {
    console.warn('Failed to save chat messages to storage:', e);
  }
};

/**
 * Drop a plan's stored thread.
 *
 * The deliberate counterpart to saveMessagesToStorage's empty-list no-op:
 * deletion happens only where a human asked for it (Clear chat history).
 */
export const clearMessagesFromStorage = (academicPlanId: number | string | null): void => {
  if (!academicPlanId) return;
  try {
    sessionStorage.removeItem(getChatStorageKey(academicPlanId));
  } catch (e) {
    console.warn('Failed to clear chat messages from storage:', e);
  }
};

/**
 * Create a user message object.
 */
export const createUserMessage = (content: string): ChatMessage => ({
  id: Date.now(),
  role: 'user',
  content: content.trim(),
  timestamp: new Date().toISOString()
});

/**
 * Create a streaming assistant message placeholder.
 */
export const createStreamingAssistantMessage = (): { message: ChatMessage; id: number } => {
  const id = Date.now() + 1;
  return {
    message: {
      id,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    },
    id
  };
};

/**
 * Default fallback models when API fails.
 */
export const FALLBACK_MODELS: ChatModel[] = [
  {
    id: 'sonnet-3.5',
    name: 'Claude Sonnet 5.5',
    description: 'Most advanced - Best for complex academic planning',
    default: true
  },
  {
    id: 'haiku',
    name: 'Claude Haiku 5.5',
    description: 'Fast and economical - Good for simple questions',
    default: false
  }
];
